import React, { ReactElement, useState, useEffect, useCallback } from 'react';
import { AxiosResponse } from 'axios';

import { createUrl, request } from 'app/utils';

import { Flex, Box, Typography, Section, Loading, Page, SearchBar } from 'components';
import { useProducts, ProductsApiResponse, ProductsList } from 'features/products';
import { useSchool } from 'features/school';
import { useMenu } from 'features/menu';

import usestorage from 'app/utils/storage';
import { useSearchParams } from 'react-router-dom';

const ProductsListView = (): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [loadingEdit, setLoadingEdit] = useState(true);

  const { apiGatewayUrl, schoolUuid } = useSchool();
  const { products, setProducts, clearProducts, totalPages } = useProducts();
  const { setBackPathname, clearBackPathname } = useMenu();

  const storage = usestorage('localStorage');
  const currentDashboard = storage.get('currentDashboard');

  const [searchParams, setSearchParams] = useSearchParams();

  const currentDashboardArea = searchParams.get('dashboardAreaUuid');
  const searchText = searchParams.get('search');

  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(50);

  const fetchCourses = useCallback(async () => {
    setLoadingEdit(true);

    await Promise.all([
      request({
        url: '/api/v1/School/SearchCourses',
        method: 'get',
        baseURL: apiGatewayUrl,
        params: {
          ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
          ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
          searchFor: searchText,
          page,
          size,
        },
      }).then((response: AxiosResponse<ProductsApiResponse>) => {
        setProducts(response.data);
      }),
    ])
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        setLoadingEdit(false);
        setLoading(false);
      });
  }, [searchText, page, size]);

  useEffect(() => {
    fetchCourses();
  }, [searchText, page, size]);

  useEffect(() => {
    setBackPathname(createUrl('dashboard'));

    return () => {
      clearProducts();
      clearBackPathname();
    };
  }, []);

  const isBenndorf = schoolUuid === 'cfaf550b-037d-45b2-9f84-979fb01185d4';
  // const isG10 = schoolUuid === '14d970bd-3794-4268-bf67-2c5d2d0f0285';
  const isMeuPlanoCrypto = schoolUuid === '20b929ad-c773-46e2-93de-991988587835';

  let hat = 'Nossos cursos';
  let title = 'Conheça todos os nossos cursos';
  let pageTitle = 'Todos os cursos';

  if (isBenndorf) {
    hat = 'Nossos produtos';
    title = 'Conheça todos os nossos produtos';
    pageTitle = 'Todos os produtos';
  } else if (isMeuPlanoCrypto) {
    hat = '';
    title = 'Conheça todos os nossos conteúdos';
    pageTitle = 'Todos os conteúdos';
  }

  if (loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title={pageTitle}>
      {loadingEdit && <Loading alpha />}

      <Flex py={{ m: 30, t: 60, d: 90 }} bg="gray_100" flexGrow={0}>
        <Section flexDirection="column">
          <Flex justifyContent="space-between" flexWrap={{ m: 'wrap', d: 'nowrap' }} alignItems="flex-end">
            <Flex flexDirection="column">
              {!!hat && (
                <Typography type="type3" upper mb={2}>
                  {hat}
                </Typography>
              )}

              <Typography type="type8" fontWeight="bold">
                {title}
              </Typography>
            </Flex>

            <Box width="auto" flexGrow={0} justifyContent="flex-end">
              {/* <Link to={createUrl('productsList')}>
              <Button mt={{ m: 20 }}>Todos os cursos</Button>
            </Link> */}
            </Box>
          </Flex>

          <Flex mt={30} mb={20} height={60} alignItems="center" justifyContent="center">
            <SearchBar
              searchText={searchText || ''}
              searchPlaceholder="Buscar curso"
              onSearchTextChange={(search) => {
                setPage(1);
                setSearchParams(`?${new URLSearchParams({ search })}`);
              }}
              setPage={setPage}
              totalPages={totalPages || 1}
              currentPage={page}
              selectedRows={false}
              filters={[
                {
                  selectedValue: size,
                  onChange: (changedSize) => {
                    setPage(1);
                    setSize(Number(changedSize));
                  },
                  options: [
                    { value: 20, label: '20' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                  ],
                  minWidth: 150,
                },
              ]}
            />
          </Flex>

          <ProductsList products={products} />
        </Section>
      </Flex>
    </Page>
  );
};

export default ProductsListView;
